import { ElementDto } from "@repo/api/overlays";
import { EasingFunction, interpolate, useCurrentFrame } from "remotion";
import { ElementTiming } from "./timing.ts";
import { getTransitionEasing } from "./scene-transition.ts";

interface InterpolatedValueProps {
  startFrame: number;
  animationDurationFrames: number;
  valueFrom: number;
  valueTo: number;
  currentFrame: number;
  easing: EasingFunction;
}

export function calculateInterpolatedValue({
  startFrame,
  animationDurationFrames,
  valueFrom,
  valueTo,
  currentFrame,
  easing,
}: InterpolatedValueProps) {
  const relativeFrame = currentFrame - startFrame;

  if (relativeFrame < 0) {
    return valueFrom;
  }

  if (animationDurationFrames < 1) {
    return valueTo;
  }

  return interpolate(
    relativeFrame,
    [0, animationDurationFrames],
    [valueFrom, valueTo],
    {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
      easing,
    },
  );
}

interface UseOpacityProps {
  element: ElementDto;
  elementTiming: ElementTiming | null;
}
export function useOpacity({ element, elementTiming }: UseOpacityProps) {
  const currentFrame = useCurrentFrame();

  if (!elementTiming) {
    return element.opacity;
  }

  const entryAnimation = element.enterAt.animation;

  if (entryAnimation && entryAnimation.kind === "FADE_IN") {
    const entryAnimationStartFrame = elementTiming.sceneStartAtFrame;
    const entryAnimationEndFrame =
      entryAnimationStartFrame + elementTiming.entryTransitionFrames;

    if (
      currentFrame >= entryAnimationStartFrame &&
      currentFrame <= entryAnimationEndFrame
    ) {
      return calculateInterpolatedValue({
        currentFrame,
        startFrame: entryAnimationStartFrame,
        animationDurationFrames: elementTiming.entryTransitionFrames,
        valueFrom: 0,
        valueTo: element.opacity,
        easing: getTransitionEasing(entryAnimation),
      });
    }
  }

  const exitAnimation = element.exitAt.animation;

  if (exitAnimation && exitAnimation.kind === "FADE_OUT") {
    const exitAnimationStartFrame =
      elementTiming.sceneEndAtFrame - elementTiming.exitTransitionFrames;
    const exitAnimationEndFrame = elementTiming.sceneEndAtFrame;

    if (
      currentFrame >= exitAnimationStartFrame &&
      currentFrame <= exitAnimationEndFrame
    ) {
      return calculateInterpolatedValue({
        currentFrame,
        startFrame: exitAnimationStartFrame,
        animationDurationFrames: elementTiming.exitTransitionFrames,
        valueFrom: element.opacity,
        valueTo: 0,
        easing: getTransitionEasing(exitAnimation),
      });
    }
  }

  return element.opacity;
}
